/**
 * ArtsProfile — /arts/:section/:slug
 * Detail page for one arts entity (artist, musician, museum, festival etc.)
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, MapPin, Mail, Phone, Globe, Award, Film, Music, Star, Loader2,
  PlaySquare, ExternalLink, LayoutTemplate, ShoppingBag, Plus, Trash2,
} from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import AutoLinkText from "@/components/AutoLinkText";
import MediaLightbox from "@/components/MediaLightbox";
import MediaEmbed from "@/components/MediaEmbed";

export default function ArtsProfile() {
  const { section, slug } = useParams();
  const { user } = useAuth();
  const [entity, setEntity] = useState(null);
  const [awardsWon, setAwardsWon] = useState([]);
  const [products, setProducts] = useState([]);
  const [showProdForm, setShowProdForm] = useState(false);
  const [prodBusy, setProdBusy] = useState(false);
  const [prodForm, setProdForm] = useState({ name: "", price: "", currency: "usd", category: "Painting", image_url: "", description: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [siteBusy, setSiteBusy] = useState(false);
  const isOwner = user && entity && (entity.created_by === user.id);

  const reloadProducts = async () => {
    try {
      const { data } = await http.get(`/arts/entities/${encodeURIComponent(slug)}/products`);
      setProducts(data.items || []);
    } catch { setProducts([]); }
  };

  const addProduct = async (e) => {
    e.preventDefault();
    if (!prodForm.name || !prodForm.price) { toast.error("Name and price are required"); return; }
    setProdBusy(true);
    try {
      await http.post(`/arts/entities/${encodeURIComponent(slug)}/products`, {
        name:        prodForm.name,
        price:       Number(prodForm.price),
        currency:    prodForm.currency,
        category:    prodForm.category,
        image_url:   prodForm.image_url || undefined,
        description: prodForm.description || undefined,
      });
      toast.success("Product listed");
      setProdForm({ name: "", price: "", currency: "usd", category: "Painting", image_url: "", description: "" });
      setShowProdForm(false);
      await reloadProducts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not list product");
    } finally {
      setProdBusy(false);
    }
  };

  const removeProduct = async (productId) => {
    if (!confirm("Remove this product from your marketplace?")) return;
    try {
      await http.delete(`/arts/products/${productId}`);
      toast.success("Removed");
      await reloadProducts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove");
    }
  };

  const buildWebsite = async () => {
    if (!user) {
      toast.error("Please sign in to build your own website");
      return;
    }
    if (!entity) return;
    setSiteBusy(true);
    try {
      const { data } = await http.post("/websites/from-source", {
        source_type: "arts_entity",
        source_id:   entity.id || entity.slug,
        template:    "minimal",
        theme:       "cream",
        layout:      "multi_page",
      });
      const site = data?.site;
      if (site?.id) {
        window.location.assign(`/my/websites/${site.id}/edit`);
      } else if (site?.slug) {
        window.location.assign(`/my/websites`);
      } else {
        toast.success("Website created");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "";
      if (msg.startsWith("Only the owner")) {
        toast.error("Only the profile owner can build a website from it");
      } else {
        toast.error(msg || "Could not create the website");
      }
    } finally {
      setSiteBusy(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await http.get(`/arts/entities/${encodeURIComponent(slug)}`);
        if (alive) setEntity(data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Not found");
      } finally {
        if (alive) setLoading(false);
      }
      // Awards this entity has received (separate call, non-blocking)
      try {
        const { data } = await http.get(`/arts/entities/${encodeURIComponent(slug)}/awards`);
        if (alive) setAwardsWon(data.items || []);
      } catch { /* ignore */ }
      // Marketplace products
      try {
        const { data } = await http.get(`/arts/entities/${encodeURIComponent(slug)}/products`);
        if (alive) setProducts(data.items || []);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [slug]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="arts-profile">
      <Link to={`/arts/${section}`} className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> Back to {section}
      </Link>

      {loading && (
        <div className="wofy-card !p-6 inline-flex items-center gap-2" data-testid="arts-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading profile…
        </div>
      )}

      {!loading && error && (
        <div className="wofy-card !p-6" style={{ background: "#FEF3C7" }}>{error}</div>
      )}

      {!loading && entity && (
        <>
          <header className="mb-6">
            <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-2">
              {entity.section}{entity.category ? ` · ${entity.category}` : ""}
            </div>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="font-display text-4xl leading-tight">{entity.name}</h1>
                {entity.tagline && (
                  <p className="text-lg opacity-90 mt-1">{entity.tagline}</p>
                )}
                {(entity.city || entity.country) && (
                  <div className="text-sm opacity-70 mt-2 inline-flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {[entity.city, entity.state, entity.country].filter(Boolean).join(", ")}
                  </div>
                )}
              </div>
              {entity.is_featured && (
                <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full inline-flex items-center gap-1"
                      style={{ background: "var(--wofy-primary)", color: "white" }}>
                  <Star className="w-3 h-3 fill-current" /> Featured
                </span>
              )}
            </div>
            {entity.aliases?.length > 0 && (
              <div className="text-xs opacity-70 mt-2">
                Also known as: {entity.aliases.slice(0, 5).join(" · ")}
              </div>
            )}
          </header>

          {entity.photo_url && (
            <img
              src={entity.photo_url}
              alt={entity.name}
              className="w-full max-h-96 object-cover rounded-xl mb-6"
              data-testid="arts-hero-image"
            />
          )}

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-6">
            <div className="min-w-0 space-y-6">
              {entity.short_bio && (
                <section data-testid="arts-short-bio">
                  <p className="text-lg opacity-90"><AutoLinkText text={entity.short_bio} perEntityMax={2} /></p>
                </section>
              )}
              {entity.long_bio && (
                <section data-testid="arts-long-bio">
                  <h2 className="font-display text-2xl mb-2">Biography</h2>
                  <p className="whitespace-pre-line opacity-90"><AutoLinkText text={entity.long_bio} perEntityMax={3} /></p>
                </section>
              )}

              {entity.awards?.length > 0 && (
                <section data-testid="arts-awards">
                  <h2 className="font-display text-2xl mb-2 inline-flex items-center gap-1.5">
                    <Award className="w-5 h-5" /> Awards
                  </h2>
                  <ul className="space-y-1">
                    {entity.awards.map((a, i) => (
                      <li key={i} className="text-sm opacity-90">
                        <strong>{a.name}</strong>
                        {a.year ? ` · ${a.year}` : ""}
                        {a.work ? ` — ${a.work}` : ""}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {awardsWon.length > 0 && (
                <section data-testid="arts-awards-received">
                  <h2 className="font-display text-2xl mb-2 inline-flex items-center gap-1.5">
                    <Award className="w-5 h-5" /> Recognised by
                  </h2>
                  <ul className="space-y-1">
                    {awardsWon.map((a, i) => (
                      <li key={i} className="text-sm opacity-90">
                        <Link to={`/arts/awards/${a.award_slug}`} className="hover:underline font-medium"
                              style={{ color: "var(--wofy-primary)" }}
                              data-testid={`arts-award-link-${a.award_slug}`}>
                          {a.award_name}
                        </Link>
                        {a.year ? ` · ${a.year}` : ""}
                        {a.category ? ` — ${a.category}` : ""}
                        {a.work ? ` (${a.work})` : ""}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {entity.filmography?.length > 0 && (
                <section data-testid="arts-filmography">
                  <h2 className="font-display text-2xl mb-2 inline-flex items-center gap-1.5">
                    <Film className="w-5 h-5" /> Filmography
                  </h2>
                  <ul className="space-y-1">
                    {entity.filmography.map((f, i) => (
                      <li key={i} className="text-sm opacity-90">
                        <strong>{f.title}</strong>
                        {f.year ? ` (${f.year})` : ""}
                        {f.role ? ` — ${f.role}` : ""}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {entity.discography?.length > 0 && (
                <section data-testid="arts-discography">
                  <h2 className="font-display text-2xl mb-2 inline-flex items-center gap-1.5">
                    <Music className="w-5 h-5" /> Discography
                  </h2>
                  <ul className="space-y-1">
                    {entity.discography.map((a, i) => (
                      <li key={i} className="text-sm opacity-90">
                        <strong>{a.title}</strong>
                        {a.year ? ` (${a.year})` : ""}
                        {a.label ? ` — ${a.label}` : ""}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {entity.portfolio?.length > 0 && (
                <section data-testid="arts-portfolio">
                  <h2 className="font-display text-2xl mb-2">Portfolio</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {entity.portfolio.map((p, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setLightboxIdx(i)}
                        data-testid={`arts-portfolio-thumb-${i}`}
                        className="text-left group"
                      >
                        <img
                          src={p.image_url}
                          alt={p.caption || entity.name}
                          className="w-full h-40 object-cover rounded-lg transition group-hover:opacity-90"
                        />
                        {p.caption && (
                          <figcaption className="text-xs opacity-70 mt-1 group-hover:opacity-100">
                            {p.caption}
                          </figcaption>
                        )}
                      </button>
                    ))}
                  </div>
                  <MediaLightbox
                    images={entity.portfolio}
                    openIndex={lightboxIdx}
                    onClose={() => setLightboxIdx(null)}
                  />
                </section>
              )}

              {entity.videos?.length > 0 && (
                <section data-testid="arts-videos">
                  <h2 className="font-display text-2xl mb-2 inline-flex items-center gap-1.5">
                    <PlaySquare className="w-5 h-5" /> Videos & audio
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {entity.videos.map((v, i) => (
                      <div key={i} data-testid={`arts-video-${i}`}>
                        <MediaEmbed url={v.url} title={v.title || entity.name} />
                        {v.title && <div className="text-xs opacity-70 mt-1">{v.title}</div>}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {(products.length > 0 || isOwner) && (
                <section data-testid="arts-marketplace-section">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="font-display text-2xl inline-flex items-center gap-1.5">
                      <ShoppingBag className="w-5 h-5" /> Marketplace
                    </h2>
                    {isOwner && (
                      <button type="button" onClick={() => setShowProdForm(v => !v)}
                              className="wofy-btn-ghost text-xs" data-testid="arts-product-add-toggle">
                        <Plus className="w-3 h-3" /> {showProdForm ? "Cancel" : "List product"}
                      </button>
                    )}
                  </div>

                  {isOwner && showProdForm && (
                    <form onSubmit={addProduct} className="wofy-card !p-4 mb-3 space-y-2" data-testid="arts-product-form">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <input placeholder="Product name" value={prodForm.name}
                               onChange={(e) => setProdForm(f => ({ ...f, name: e.target.value }))}
                               className="rounded border px-2 py-1.5 text-sm bg-transparent"
                               style={{ borderColor: "var(--wofy-border)" }} required data-testid="arts-product-name" />
                        <div className="flex gap-2">
                          <input type="number" step="0.01" min="0.5" placeholder="Price"
                                 value={prodForm.price} onChange={(e) => setProdForm(f => ({ ...f, price: e.target.value }))}
                                 className="rounded border px-2 py-1.5 text-sm bg-transparent flex-1"
                                 style={{ borderColor: "var(--wofy-border)" }} required data-testid="arts-product-price" />
                          <select value={prodForm.currency}
                                  onChange={(e) => setProdForm(f => ({ ...f, currency: e.target.value }))}
                                  className="rounded border px-2 py-1.5 text-sm bg-transparent"
                                  style={{ borderColor: "var(--wofy-border)" }} data-testid="arts-product-currency">
                            <option>usd</option><option>eur</option><option>gbp</option><option>inr</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <select value={prodForm.category}
                                onChange={(e) => setProdForm(f => ({ ...f, category: e.target.value }))}
                                className="rounded border px-2 py-1.5 text-sm bg-transparent"
                                style={{ borderColor: "var(--wofy-border)" }} data-testid="arts-product-category">
                          {["Painting","Print","Sculpture","Photography Print","Digital Art","Book","Music Album","Vinyl","Ticket","Merchandise","Craft","Other"].map(k =>
                            <option key={k} value={k}>{k}</option>)}
                        </select>
                        <input type="url" placeholder="Image URL" value={prodForm.image_url}
                               onChange={(e) => setProdForm(f => ({ ...f, image_url: e.target.value }))}
                               className="rounded border px-2 py-1.5 text-sm bg-transparent"
                               style={{ borderColor: "var(--wofy-border)" }} data-testid="arts-product-image" />
                      </div>
                      <textarea placeholder="Short description" value={prodForm.description} rows={2}
                                onChange={(e) => setProdForm(f => ({ ...f, description: e.target.value }))}
                                className="w-full rounded border px-2 py-1.5 text-sm bg-transparent"
                                style={{ borderColor: "var(--wofy-border)" }} data-testid="arts-product-description" />
                      <div className="flex justify-end">
                        <button type="submit" disabled={prodBusy} className="wofy-btn-primary text-sm" data-testid="arts-product-submit">
                          {prodBusy ? "Publishing…" : "List product"}
                        </button>
                      </div>
                    </form>
                  )}

                  {products.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="arts-products-grid">
                      {products.map((p) => (
                        <div key={p.id} className="rounded-xl border overflow-hidden relative"
                             style={{ borderColor: "var(--wofy-border)" }} data-testid={`arts-product-${p.id}`}>
                          {isOwner && (
                            <button type="button" onClick={() => removeProduct(p.id)}
                                    className="absolute top-1 right-1 bg-white/90 rounded-full p-1 shadow"
                                    aria-label="Remove" data-testid={`arts-product-remove-${p.id}`}>
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </button>
                          )}
                          <Link to={`/product/${p.id}`}>
                            {p.image_url ? (
                              <img src={p.image_url} alt={p.name} className="w-full h-40 object-cover" />
                            ) : (
                              <div className="w-full h-40 bg-black/5" />
                            )}
                            <div className="p-3">
                              <div className="text-[10px] font-mono uppercase tracking-wider opacity-60">{p.category}</div>
                              <div className="font-medium text-sm line-clamp-2">{p.name}</div>
                              <div className="mt-1 text-sm font-semibold" style={{ color: "var(--wofy-primary)" }}>
                                {(p.currency || "usd").toUpperCase()} {p.price?.toFixed(2)}
                              </div>
                            </div>
                          </Link>
                        </div>
                      ))}
                    </div>
                  )}
                  {products.length === 0 && !isOwner && (
                    <div className="text-sm opacity-70">No products listed yet.</div>
                  )}
                </section>
              )}
            </div>

            {/* Right sidebar */}
            <aside className="lg:sticky lg:top-24 self-start space-y-4" data-testid="arts-sidebar">
              {user && entity.created_by === user.id && (
                <div className="wofy-card !p-4"
                     style={{ background: "var(--wofy-primary-soft, rgba(178,93,56,0.08))" }}
                     data-testid="arts-build-site-card">
                  <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>
                    <LayoutTemplate className="w-3.5 h-3.5 inline mr-1" /> BUILD YOUR OWN SITE
                  </div>
                  <p className="text-xs opacity-85 mb-3">
                    Spin up a personal website pre-filled with your bio, portfolio and
                    videos — edit pages, add links, publish in minutes.
                  </p>
                  <button
                    type="button"
                    onClick={buildWebsite}
                    disabled={siteBusy}
                    className="wofy-btn-primary w-full text-sm"
                    data-testid="arts-build-site-btn"
                  >
                    {siteBusy ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</>
                    ) : (
                      <><LayoutTemplate className="w-4 h-4" /> Create my website</>
                    )}
                  </button>
                </div>
              )}
              <div className="wofy-card !p-4">
                <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>QUICK FACTS</div>
                <dl className="space-y-2 text-sm">
                  {entity.born && <div><dt className="opacity-60 text-xs">Born</dt><dd>{entity.born}</dd></div>}
                  {entity.genres?.length > 0 && (
                    <div><dt className="opacity-60 text-xs">Genres</dt><dd>{entity.genres.join(", ")}</dd></div>
                  )}
                  {entity.instruments?.length > 0 && (
                    <div><dt className="opacity-60 text-xs">Instruments</dt><dd>{entity.instruments.join(", ")}</dd></div>
                  )}
                  {entity.styles?.length > 0 && (
                    <div><dt className="opacity-60 text-xs">Styles</dt><dd>{entity.styles.join(", ")}</dd></div>
                  )}
                  {entity.languages?.length > 0 && (
                    <div><dt className="opacity-60 text-xs">Languages</dt><dd>{entity.languages.join(", ")}</dd></div>
                  )}
                </dl>
              </div>

              {(entity.contact_email || entity.contact_phone || entity.website) && (
                <div className="wofy-card !p-4">
                  <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>CONTACT</div>
                  <div className="space-y-2 text-sm">
                    {entity.website && (
                      <a href={entity.website} target="_blank" rel="noopener noreferrer"
                         className="inline-flex items-center gap-1.5 hover:underline"
                         style={{ color: "var(--wofy-primary)" }} data-testid="arts-website">
                        <Globe className="w-3.5 h-3.5" /> Website
                      </a>
                    )}
                    {entity.contact_email && (
                      <div className="inline-flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5 opacity-60" />
                        <a href={`mailto:${entity.contact_email}`} className="hover:underline">{entity.contact_email}</a>
                      </div>
                    )}
                    {entity.contact_phone && (
                      <div className="inline-flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5 opacity-60" /> {entity.contact_phone}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {entity.social && Object.values(entity.social).some(Boolean) && (
                <div className="wofy-card !p-4">
                  <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>SOCIAL</div>
                  <ul className="text-sm space-y-1">
                    {Object.entries(entity.social).filter(([_, v]) => v).map(([k, v]) => (
                      <li key={k}>
                        <a href={v} target="_blank" rel="noopener noreferrer" className="hover:underline"
                           style={{ color: "var(--wofy-primary)" }}>
                          {k}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
